import { Typography } from '@material-ui/core';
import { Lock, MonetizationOn } from '@material-ui/icons';
import { FormikTextField } from '@superdispatch/forms';
import { Stack } from '@superdispatch/ui';
import { Box, DescriptionItem, TextBox } from '@superdispatch/ui-lab';
import { useField } from 'formik';
import { useAppFormik } from 'shared/form/AppFormik';
import { WebViewLink } from 'shared/routing/WebViewLinks';
import { hasOnlyDigits } from 'shared/utils/StringUtils';
import { yupObject, yupString } from 'shared/utils/YupUtils';
import { StringSchema } from 'yup';
import routingAccountIllustration from '../../assets/routing_account.png';
import { useCreateBankAccount } from '../../data/MoovAPI';
import { trackSuperPayEvent } from '../../data/SuperPayAnalytics';
import { useSuperPaySettings } from '../../data/SuperPayAPI';
import { logPaymentInfo } from '../PaymentLogger';
import { FormikRoutingNumberField } from './FormikRoutingNumberField';

const NUMBERS_MISMATCH_ERROR = "Numbers don't match";

export const bankAccountValidationSchema = yupObject({
  holderName: yupString().required(),
  routingNumber: yupString()
    .required()
    .length(9, 'Routing number must have 9 digits')
    .test('only digits', 'Invalid routing number', hasOnlyDigits),
  accountNumber: yupString()
    .required()
    .min(5, 'Account number must have more than 4 digits')
    .max(17, 'Account number must have 17 or less digits')
    .test('only digits', 'Invalid bank account number', hasOnlyDigits),
  confirmAccountNumber: yupString()
    .when(
      'accountNumber',
      (accountNumber: string | undefined, schema: StringSchema) =>
        schema.test(
          'match',
          NUMBERS_MISMATCH_ERROR,
          (confirmAccountNumber) => accountNumber === confirmAccountNumber,
        ),
    )
    .required(),
});

interface Props {
  onSettled: () => void;
}

export function useBankAccountForm({ onSettled }: Props) {
  const { mutateAsync: createBankAccount } = useCreateBankAccount();

  return useAppFormik({
    validationSchema: bankAccountValidationSchema,
    onSubmit: ({ holderName, accountNumber, routingNumber }) => {
      return createBankAccount({ holderName, accountNumber, routingNumber });
    },
    onSubmitSuccess: () => {
      trackSuperPayEvent({ name: 'Carrier Submitted Bank Account' });

      logPaymentInfo('Carrier Submitted Bank Account', 'BankAccountForm');
      onSettled();
    },
    onSubmitFailure: () => {
      onSettled();
    },
  });
}

interface BankAccountSetupFormProps {
  onVerificationRequired: () => void;
}

export function useBankAccountSetupForm({
  onVerificationRequired,
}: BankAccountSetupFormProps) {
  const { refetch: refetchSuperPaySetting, isFetching } = useSuperPaySettings();

  const formik = useBankAccountForm({
    onSettled: () => {
      void refetchSuperPaySetting().then(({ data }) => {
        const bankAccount = data?.bank_accounts?.[0];
        if (bankAccount && bankAccount.verification_method !== 'ach') {
          onVerificationRequired();
        }
      });
    },
  });

  return { formik, isFetching };
}

export function BankAccountLegalNotice() {
  return (
    <TextBox color="secondary">
      To enable payments, we are legally required to collect information below.
      This information is not stored by Super Dispatch and only used to verify
      the business.
    </TextBox>
  );
}

export function BankAccountFormFields() {
  const [_, { error, touched }] = useField('confirmAccountNumber');
  const hasAccountNumbersMatchError =
    error === NUMBERS_MISMATCH_ERROR && touched;
  return (
    <Stack space="small">
      <FormikTextField
        label="Account Holder Name"
        name="holderName"
        fullWidth={true}
      />

      <Box maxWidth="100%">
        <FormikRoutingNumberField
          label={
            <>
              <Typography component="span">Routing Number</Typography>{' '}
              <Typography color="textSecondary" component="span">
                (US banks only)
              </Typography>
            </>
          }
          name="routingNumber"
          fullWidth={true}
        />
      </Box>
      <FormikTextField
        label="Account Number"
        name="accountNumber"
        helperText={
          hasAccountNumbersMatchError
            ? ' '
            : 'A 5-17 digit number next to the routing number'
        }
        fullWidth={true}
        error={hasAccountNumbersMatchError}
      />

      <FormikTextField
        label="Confirm Account Number"
        name="confirmAccountNumber"
        fullWidth={true}
      />
    </Stack>
  );
}

export function BankAccountForm() {
  return (
    <>
      <Stack space="small">
        <Stack space="xsmall">
          <DescriptionItem
            icon={<Lock />}
            label="Your personal data is encrypted and private."
            wrap={true}
          />
          <DescriptionItem
            icon={<MonetizationOn />}
            label={
              <Typography>
                We will make{' '}
                <WebViewLink
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://support.superdispatch.com/hc/articles/36590363663251-SuperPay-Micro-Transactions"
                  color="primary"
                >
                  a small transaction
                </WebViewLink>{' '}
                into your bank account to verify it.
              </Typography>
            }
            wrap={true}
          />
        </Stack>

        <BankAccountFormFields />
      </Stack>

      <Box marginTop="large">
        <Stack>
          <Typography color="textSecondary">
            These numbers can be found at the bottom of a check or by contacting
            your bank.
          </Typography>

          <img
            src={routingAccountIllustration}
            width="292px"
            alt="routing account illustration"
          />
        </Stack>
      </Box>
    </>
  );
}
